import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type HelpChatContextValue = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
};

const HelpChatContext = createContext<HelpChatContextValue | null>(null);

export function HelpChatProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, close]);

  const value = useMemo(
    () => ({ isOpen, open, close, toggle }),
    [isOpen, open, close, toggle],
  );

  return <HelpChatContext.Provider value={value}>{children}</HelpChatContext.Provider>;
}

export function useHelpChat(): HelpChatContextValue {
  const context = useContext(HelpChatContext);
  if (!context) {
    throw new Error('useHelpChat must be used within HelpChatProvider');
  }
  return context;
}
